import { useState, useMemo, useCallback, useRef, useEffect } from "react";

export default function useVirtualScroll({
  data,
  fields = [],
  colWidths = {},
  rowHeight = 35,
  height = 400,
  overscanY = 10, // Render 10 extra rows up/down
  overscanX = 3, // Render 3 extra columns left/right
  numberColWidth = 70, // Width of the 'No' column
  addBtnWidth = 50, // Width of the '+' button at the end
  containerRef,
}) {
  const [scroll, setScroll] = useState({ top: 0, left: 0, clientWidth: 1200 });
  const rafRef = useRef(null);

  // Initialize client width on mount so horizontal virtualization knows the screen size
  useEffect(() => {
    if (containerRef?.current) {
      setScroll((s) => ({
        ...s,
        clientWidth: containerRef.current.clientWidth,
      }));
    }
  }, [containerRef]);

  // --- 1. HORIZONTAL MATH (Cumulative Widths) ---
  const columnData = useMemo(() => {
    let currentLeft = numberColWidth; // Data columns start AFTER the number column
    const positions = fields.map((field, index) => {
      const width = colWidths[field.key] || 160;
      const pos = { ...field, width, left: currentLeft, colIndex: index };
      currentLeft += width;
      return pos;
    });
    // Total row width includes No Col, Data Cols, and the Add Button
    const totalWidth = currentLeft + addBtnWidth;
    return { positions, totalWidth };
  }, [fields, colWidths, numberColWidth, addBtnWidth]);

  // --- 2. VERTICAL VIRTUALIZATION ---
  const rawStartIndex = Math.floor(scroll.top / rowHeight);
  const visibleCount = Math.ceil(height / rowHeight) * 2;
  const startIndex = Math.max(0, rawStartIndex - overscanY);
  const endIndex = Math.min(
    data.length,
    rawStartIndex + visibleCount + overscanY,
  );

  const visibleRows = useMemo(
    () => data.slice(startIndex, endIndex),
    [data, startIndex, endIndex],
  );

  // --- 3. HORIZONTAL VIRTUALIZATION ---
  const viewStart = scroll.left;
  const viewEnd = scroll.left + scroll.clientWidth;

  let startColIndex = 0;
  let endColIndex = columnData.positions.length;

  // Find first visible column
  for (let i = 0; i < columnData.positions.length; i++) {
    if (
      columnData.positions[i].left + columnData.positions[i].width >=
      viewStart
    ) {
      startColIndex = Math.max(0, i - overscanX);
      break;
    }
  }
  // Find last visible column
  for (let i = startColIndex; i < columnData.positions.length; i++) {
    if (columnData.positions[i].left > viewEnd) {
      endColIndex = Math.min(columnData.positions.length, i + overscanX + 1);
      break;
    }
  }

  const visibleFields = useMemo(
    () => columnData.positions.slice(startColIndex, endColIndex),
    [columnData.positions, startColIndex, endColIndex],
  );

  const offsetY = startIndex * rowHeight;
  const totalHeight = data.length * rowHeight;

  const handleScroll = useCallback((e) => {
    const top = e.target.scrollTop;
    const left = e.target.scrollLeft;
    const clientWidth = e.target.clientWidth;

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      setScroll({ top, left, clientWidth });
    });
  }, []);

  return {
    visibleRows,
    visibleFields,
    totalWidth: columnData.totalWidth,
    offsetY,
    handleScroll,
    totalHeight,
  };
}
